/**
 * The rest of what a table MEANS (Olivia's 2g, after `tableFormat.ts`): group
 * headers, notes, row groups and long tables. Like the column format, all of it
 * is stored in the table, travels into every paper and into Copy LaTeX, and the
 * table's LaTeX is the same under every theme.
 *
 * Stored in the table content:
 *   - `headerGroups: [{ label, start, span }]` — a row above the header with the
 *     label over grid columns start … start+span-1 (0-based), and a trimmed
 *     `\cmidrule(lr)` under each. Groups that overlap, run off the table or sit
 *     on a table without a header are not drawn or written.
 *   - `note` on a cell (`{ content, note }`, header or body) — a `\tnote` mark in
 *     the cell, the note under the table (`threeparttable`).
 *   - `rowGroups: [i, …]` — body rows (0-based) that start a group: a rule before
 *     each; a Banded paper restarts its stripes after it.
 *
 * Derived, never stored: the note marks (a, b, c… in reading order — header left
 * to right, then the body row by row, by each cell's index in its row; one note
 * text written twice shares its mark), and whether the table is long: more than
 * `LONG_TABLE_ROWS` body rows (or `longTable: true`), a `longtable` whose header
 * repeats on every page under "(continued)".
 *
 * The server mirrors this in `Lilia.Engines/TableLayout.cs`; change both
 * together.
 */
import { placeRow, type Cell } from "./tableCells";

export interface HeaderGroup {
  label: string;
  /** First grid column, 0-based. */
  start: number;
  /** Columns it spans, at least 1. */
  span: number;
}

export interface TableNote {
  mark: string;
  text: string;
}

/** A table with more body rows than this breaks across pages as a longtable. */
export const LONG_TABLE_ROWS = 40;

/** The parts of a table the layout reads. */
interface LayoutSource {
  hasHeader: boolean;
  header: readonly Cell[];
  rows: readonly (readonly Cell[])[];
  groups?: readonly HeaderGroup[];
  rowGroups?: readonly number[];
  longTable?: boolean;
}

/** a … z, then aa, ab … */
export function markFor(i: number): string {
  return i < 26 ? String.fromCharCode(97 + i) : markFor(Math.floor(i / 26) - 1) + String.fromCharCode(97 + (i % 26));
}

/** The groups that can be drawn: inside the table, at least one column, never overlapping, in order. */
export function validGroups(t: Pick<LayoutSource, "groups" | "hasHeader">, width: number): HeaderGroup[] {
  if (!t.hasHeader || !t.groups?.length) return [];
  const out: HeaderGroup[] = [];
  let end = 0;
  for (const g of [...t.groups].sort((a, b) => a.start - b.start)) {
    if (!(g.start >= 0 && g.span >= 1 && g.start + g.span <= width) || g.start < end) continue;
    out.push(g);
    end = g.start + g.span;
  }
  return out;
}

/** Body rows that start a group: past the first, inside the table. */
export function groupStarts(t: Pick<LayoutSource, "rowGroups" | "rows">): Set<number> {
  return new Set((t.rowGroups ?? []).filter((i) => Number.isInteger(i) && i > 0 && i < t.rows.length));
}

export function isLongTable(t: Pick<LayoutSource, "rows" | "longTable">): boolean {
  return t.longTable === true || t.rows.length > LONG_TABLE_ROWS;
}

/** A cell's note, trimmed; "" when it has none. */
export const cellNote = (c: Cell | undefined): string => (c?.note ?? "").trim();

export interface NoteMarks {
  /** Mark by `${row}:${index}`; row -1 is the header, index the cell's place in its row. */
  marks: Map<string, string>;
  notes: TableNote[];
}

/** The note marks, in reading order. */
export function noteMarks(t: Pick<LayoutSource, "hasHeader" | "header" | "rows">): NoteMarks {
  const marks = new Map<string, string>();
  const notes: TableNote[] = [];
  const take = (c: Cell | undefined, r: number, i: number) => {
    const text = cellNote(c);
    if (!text) return;
    let note = notes.find((n) => n.text === text);
    if (!note) {
      note = { mark: markFor(notes.length), text };
      notes.push(note);
    }
    marks.set(`${r}:${i}`, note.mark);
  };
  if (t.hasHeader) t.header.forEach((c, i) => take(c, -1, i));
  t.rows.forEach((row, r) => row.forEach((c, i) => take(c, r, i)));
  return { marks, notes };
}

/* ── edits, as pure functions over the table ─────────────────────────────── */

type Editable = LayoutSource & { header: Cell[]; rows: Cell[][] };

/** Set or clear one cell's note. Row -1 is the header. */
export function setCellNote<T extends Editable>(t: T, r: number, c: number, note: string | null): T {
  const apply = (cell: Cell): Cell => {
    const rest: Cell = { ...cell };
    delete rest.note;
    const text = (note ?? "").trim();
    return text ? { ...rest, note: text } : rest;
  };
  if (r === -1) {
    if (!t.header[c]) return t;
    const header = t.header.slice();
    header[c] = apply(header[c]);
    return { ...t, header };
  }
  if (!t.rows[r]?.[c]) return t;
  const rows = t.rows.map((x) => x.slice());
  rows[r][c] = apply(rows[r][c]);
  return { ...t, rows };
}

/** Group header columns `from` … `to` under `label`, replacing any group they overlap. */
export function groupColumns<T extends Editable>(t: T, from: number, to: number, label: string): T {
  const start = Math.min(from, to);
  const end = Math.max(from, to);
  const kept = (t.groups ?? []).filter((g) => g.start + g.span - 1 < start || g.start > end);
  const groups = [...kept, { label, start, span: end - start + 1 }].sort((a, b) => a.start - b.start);
  return { ...t, groups };
}

/** The group that holds column `c`, if any. */
export function groupAt(t: Pick<LayoutSource, "groups">, c: number): HeaderGroup | null {
  return (t.groups ?? []).find((g) => c >= g.start && c < g.start + g.span) ?? null;
}

export function ungroup<T extends Editable>(t: T, start: number): T {
  const groups = (t.groups ?? []).filter((g) => g.start !== start);
  return { ...t, groups: groups.length ? groups : undefined };
}

export function setGroupLabel<T extends Editable>(t: T, start: number, label: string): T {
  return { ...t, groups: (t.groups ?? []).map((g) => (g.start === start ? { ...g, label } : g)) };
}

/** Start a group at body row `r`, or stop one starting there. */
export function toggleRowGroup<T extends Editable>(t: T, r: number): T {
  if (r <= 0) return t;
  const set = new Set(t.rowGroups ?? []);
  if (set.has(r)) set.delete(r);
  else set.add(r);
  const rowGroups = [...set].sort((a, b) => a - b);
  return { ...t, rowGroups: rowGroups.length ? rowGroups : undefined };
}

/**
 * Keep the groups on their columns through a column insert, delete or move.
 * `map[old] = new` (null when the column is gone); `added` are new columns. A
 * group keeps the run its columns land in, taking in columns inserted inside
 * it; one whose columns no longer sit together is dropped rather than guessed.
 */
export function remapGroups(groups: readonly HeaderGroup[] | undefined, map: (old: number) => number | null, added: readonly number[] = []): HeaderGroup[] | undefined {
  if (!groups?.length) return undefined;
  const out: HeaderGroup[] = [];
  const all = groups.map((g) => ({ g, cols: Array.from({ length: g.span }, (_, k) => map(g.start + k)).filter((x): x is number => x !== null) }));
  for (const { g, cols } of all) {
    if (!cols.length) continue;
    const lo = Math.min(...cols);
    const hi = Math.max(...cols);
    const ok = Array.from({ length: hi - lo + 1 }, (_, k) => lo + k).every((c) => cols.includes(c) || added.includes(c));
    if (ok) out.push({ ...g, start: lo, span: hi - lo + 1 });
  }
  return out.length ? out : undefined;
}

/** Keep the row groups on their rows through a row insert at `at`. */
export function rowGroupsAfterInsert(rowGroups: readonly number[] | undefined, at: number): number[] | undefined {
  if (!rowGroups?.length) return undefined;
  return rowGroups.map((i) => (i >= at ? i + 1 : i));
}

/** …and a delete of row `r`: a group starting there now starts at the row that took its place. */
export function rowGroupsAfterDelete(rowGroups: readonly number[] | undefined, r: number, rowsLeft: number): number[] | undefined {
  if (!rowGroups?.length) return undefined;
  const out = [...new Set(rowGroups.map((i) => (i > r ? i - 1 : i)))].filter((i) => i > 0 && i < rowsLeft).sort((a, b) => a - b);
  return out.length ? out : undefined;
}

/**
 * A body row's place in its row group: 0 for a group's first row. Banding
 * restarts after each group's rule (lilia-theme.sty), so a preview stripes by
 * this, not by the row's index in the table.
 */
export function indexInGroup(r: number, starts: ReadonlySet<number>): number {
  let from = 0;
  for (const s of starts) if (s <= r && s > from) from = s;
  return r - from;
}

/** The group header row's cells, left to right: each group, and a blank for every column outside one. */
export function groupRowCells(groups: readonly HeaderGroup[], width: number): { start: number; span: number; label: string | null }[] {
  const out: { start: number; span: number; label: string | null }[] = [];
  let col = 0;
  for (const g of groups) {
    for (; col < g.start; col++) out.push({ start: col, span: 1, label: null });
    out.push({ start: g.start, span: g.span, label: g.label });
    col += g.span;
  }
  for (; col < width; col++) out.push({ start: col, span: 1, label: null });
  return out;
}

/**
 * The note marks by GRID column (`${row}:${column}`), for renderers that lay a
 * table out by column (the tables page's previews), where a span has moved the
 * cells after it.
 */
export function marksByColumn(t: Pick<LayoutSource, "hasHeader" | "header" | "rows">): Map<string, string> {
  const { marks } = noteMarks(t);
  const out = new Map<string, string>();
  let occupied: number[] = [];
  const walk = (row: readonly Cell[], r: number) => {
    const placed = placeRow(row, occupied);
    occupied = placed.occupied;
    row.forEach((_, i) => {
      const m = marks.get(`${r}:${i}`);
      if (m) out.set(`${r}:${placed.starts[i]}`, m);
    });
  };
  if (t.hasHeader) walk(t.header, -1);
  t.rows.forEach(walk);
  return out;
}
